import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { TrendingUp, Award, Package } from "lucide-react";

export default function CurvaABC({ insumos }) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const analise = useMemo(() => {
    // Calcular valor total de cada item
    const itensComValor = insumos.map(i => ({
      ...i,
      valorTotal: (i.quantidade_estoque || 0) * (i.custo_unitario || 0)
    })).sort((a, b) => b.valorTotal - a.valorTotal);

    const valorTotalEstoque = itensComValor.reduce((acc, i) => acc + i.valorTotal, 0);

    // Classificação ABC
    let acumulado = 0;
    const itensClassificados = itensComValor.map(item => {
      acumulado += item.valorTotal;
      const percentualAcumulado = (acumulado / valorTotalEstoque) * 100;
      let classe = "C";
      if (percentualAcumulado <= 80) classe = "A";
      else if (percentualAcumulado <= 95) classe = "B";
      
      return {
        ...item,
        percentualValor: (item.valorTotal / valorTotalEstoque) * 100,
        percentualAcumulado,
        classe
      };
    });

    const classeA = itensClassificados.filter(i => i.classe === "A");
    const classeB = itensClassificados.filter(i => i.classe === "B");
    const classeC = itensClassificados.filter(i => i.classe === "C");

    const resumo = [
      { 
        classe: "A", 
        label: "Classe A (80% valor)",
        qtdItens: classeA.length, 
        valorTotal: classeA.reduce((a, i) => a + i.valorTotal, 0),
        percentualItens: (classeA.length / itensClassificados.length) * 100,
        cor: "#ef4444"
      },
      { 
        classe: "B", 
        label: "Classe B (15% valor)",
        qtdItens: classeB.length, 
        valorTotal: classeB.reduce((a, i) => a + i.valorTotal, 0),
        percentualItens: (classeB.length / itensClassificados.length) * 100,
        cor: "#f59e0b"
      },
      { 
        classe: "C", 
        label: "Classe C (5% valor)",
        qtdItens: classeC.length, 
        valorTotal: classeC.reduce((a, i) => a + i.valorTotal, 0),
        percentualItens: (classeC.length / itensClassificados.length) * 100,
        cor: "#22c55e"
      }
    ];

    return {
      itens: itensClassificados,
      valorTotal: valorTotalEstoque,
      resumo,
      top10: itensClassificados.slice(0, 10)
    };
  }, [insumos]);

  const classeColors = {
    A: { bg: "bg-red-100 border-red-300", text: "text-red-700", badge: "bg-red-600 text-white" },
    B: { bg: "bg-amber-100 border-amber-300", text: "text-amber-700", badge: "bg-amber-500 text-white" },
    C: { bg: "bg-green-100 border-green-300", text: "text-green-700", badge: "bg-green-600 text-white" }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-stone-800">{item.nome}</p>
          <p className="text-sm text-stone-600">Valor: {formatCurrency(item.valorTotal)}</p>
          <p className="text-sm text-stone-600">{item.percentualValor?.toFixed(1)}% do estoque</p>
          <Badge className={classeColors[item.classe].badge}>Classe {item.classe}</Badge>
        </div>
      );
    }
    return null;
  };

  if (insumos.length === 0) {
    return (
      <Card className="bg-white border-stone-200">
        <CardContent className="p-12 text-center">
          <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">Nenhum item no estoque para análise</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumo por Classe */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {analise.resumo.map(r => (
          <Card key={r.classe} className={`${classeColors[r.classe].bg} border`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Award className={`w-6 h-6 ${classeColors[r.classe].text}`} />
                  <span className={`font-bold text-lg ${classeColors[r.classe].text}`}>Classe {r.classe}</span>
                </div>
                <Badge className={classeColors[r.classe].badge}>{r.qtdItens} itens</Badge>
              </div>
              <p className="text-2xl font-bold text-stone-800">{formatCurrency(r.valorTotal)}</p>
              <div className="mt-2">
                <div className="flex justify-between text-xs text-stone-600 mb-1">
                  <span>{r.percentualItens.toFixed(0)}% dos itens</span>
                  <span>{((r.valorTotal / analise.valorTotal) * 100).toFixed(0)}% do valor</span>
                </div>
                <Progress 
                  value={(r.valorTotal / analise.valorTotal) * 100} 
                  className="h-2"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Barras - Top 10 */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Top 10 Itens por Valor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analise.top10} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis 
                    type="number" 
                    tickFormatter={(v) => new Intl.NumberFormat('pt-BR', { notation: 'compact', style: 'currency', currency: 'BRL' }).format(v)}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="nome" 
                    width={100}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) => v.length > 12 ? v.substring(0, 12) + '...' : v}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="valorTotal" radius={[0, 4, 4, 0]}>
                    {analise.top10.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={
                        entry.classe === 'A' ? '#ef4444' : entry.classe === 'B' ? '#f59e0b' : '#22c55e'
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Gráfico de Pizza */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Distribuição ABC
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analise.resumo}
                    dataKey="valorTotal"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ classe, valorTotal }) => `${classe}: ${((valorTotal / analise.valorTotal) * 100).toFixed(0)}%`}
                  >
                    {analise.resumo.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.cor} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela Detalhada */}
      <Card className="bg-white border-stone-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            Análise Detalhada
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="text-left py-3 px-3 font-bold">Item</th>
                  <th className="text-center py-3 px-3 font-bold">Classe</th>
                  <th className="text-right py-3 px-3 font-bold">Estoque</th>
                  <th className="text-right py-3 px-3 font-bold">Custo Unit.</th>
                  <th className="text-right py-3 px-3 font-bold">Valor Total</th>
                  <th className="text-right py-3 px-3 font-bold">% Estoque</th>
                  <th className="text-right py-3 px-3 font-bold">% Acumulado</th>
                </tr>
              </thead>
              <tbody>
                {analise.itens.slice(0, 20).map((item, idx) => (
                  <tr key={item.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-2 px-3 font-medium">{item.nome}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge className={classeColors[item.classe].badge}>{item.classe}</Badge>
                    </td>
                    <td className="py-2 px-3 text-right">{item.quantidade_estoque} {item.unidade}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(item.custo_unitario)}</td>
                    <td className="py-2 px-3 text-right font-semibold">{formatCurrency(item.valorTotal)}</td>
                    <td className="py-2 px-3 text-right">{item.percentualValor.toFixed(1)}%</td>
                    <td className="py-2 px-3 text-right">{item.percentualAcumulado.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {analise.itens.length > 20 && (
              <p className="text-center text-stone-500 text-sm mt-4">
                Exibindo 20 de {analise.itens.length} itens
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}